// Vario-Slot: products.articles.detail.tabs.listings.detail.{VARIO_APP_ID}
//
// Otto-Modell (Spec 2026-06-18): Otto hat KEINEN fremden Listing-/EAN-Lookup und keinen
// Decide-Flow. Der Händler wählt im Slot direkt eine OTTO-Kategorie (Typeahead
// über die in den Einstellungen ausgewählten, gesyncten Kategorien) → otto_upsert_listing_from_slot
// legt die Lister-Zeile (otto.listings) an. Kein Vario-categoryRef-PATCH. Danach rendert das
// geteilte Detail-Pane (Übersicht/Marke/Vorschau) direkt im Slot.
//
// URL-Params (Vario): articleId, listingId.

import type { SupabaseClient } from '@supabase/supabase-js';
import { showToast, KF_LOGO_GOECOMMERCE } from '@vario-connect/frontend-design';
import { renderListingDetailPane } from './shared/listing-detail-pane.js';
import { openCategoryPicker, type CategoryPick } from './shared/category-picker.js';
import { awaitJob } from '../lib/await-job.js';
import { sendVarioMessage } from '../vario-iframe.js';
import { buildListerRouteTarget, readRememberedListerChannel, FOCUS_KEY } from '../lib/lister-route.js';
import { httpErrorText } from '../lib/session-expiry.js';

interface SlotCtx {
  contentEl: HTMLElement;
  supabase: SupabaseClient;
  session: { token: string; tenantId: string; marketplaceKind: string };
  supabaseUrl: string;
}

interface InboxRow { id: string; otto_category: string | null; status: string }

export async function renderListingStatus(ctx: SlotCtx): Promise<void> {
  ctx.contentEl.hidden = false;
  // Auto-Grow (main.ts): iframe = Inhaltshöhe, Vario scrollt außen. Marker löst die bridge-§16-
  // 100%-Kette (sonst spiegelt body.scrollHeight nicht den Inhalt) — identisch zu den Einstellungen.
  document.documentElement.classList.add('otto-autogrow');
  ctx.contentEl.innerHTML = renderShell();

  // Titelleisten-Speichern: ruft das aktuelle saveAll der Detailfläche (vom Pane via
  // onSaveHandler durchgereicht). Button ist nur im Lister-Modus sichtbar.
  let currentSave: (() => Promise<void>) | null = null;
  let currentSendId: string | null = null;
  ctx.contentEl.querySelector<HTMLButtonElement>('#slot-save')
    ?.addEventListener('click', () => { void currentSave?.(); });
  ctx.contentEl.querySelector<HTMLButtonElement>('#slot-send')
    ?.addEventListener('click', () => { if (currentSendId) void sendToOtto(currentSendId); });
  ctx.contentEl.querySelector<HTMLButtonElement>('#slot-open-lister')
    ?.addEventListener('click', () => { void openInLister(); });

  const params = readSlotParams();
  if (!params.articleId || !params.listingId) {
    setStatusBar(ctx.contentEl, 'error', `URL-Parameter unvollständig. articleId=${params.articleId ?? '∅'}, listingId=${params.listingId ?? '∅'}`);
    return;
  }
  const articleId = params.articleId;
  const listingId = params.listingId;

  await refresh();

  async function refresh(): Promise<void> {
    setStatusBar(ctx.contentEl, 'loading', 'Lade OTTO-Status…');
    const { data, error } = await ctx.supabase
      .schema('otto').from('listings')
      .select('id, otto_category, status')
      .eq('vario_listing_id', listingId)
      .maybeSingle();
    if (error) {
      setStatusBar(ctx.contentEl, 'error', `Status nicht ladbar: ${error.message}`);
      return;
    }
    setStatusBar(ctx.contentEl, '', '');
    const row = data as InboxRow | null;
    if (row) {
      renderInLister(row);
    } else {
      await renderPicker();
    }
  }

  // ── „Im Lister öffnen": VARIO wechselt auf den Lister-Reiter im OTTO-Verkaufskanal ──
  // Der Lister liest `otto.focus` (VARIO-Listing-ID, höchstens 30 s alt) und öffnet diese Zeile.
  // Wechselt der Host, entfernt er dieses iframe und der Rückfall-Hinweis läuft nie.
  async function openInLister(): Promise<void> {
    const { data } = await ctx.supabase.rpc('otto_list_storefronts');
    const rows = (data ?? []) as Array<{ storefront: string; vario_channel_id: string | null }>;
    const installChannelId = rows.find((r) => r.storefront === 'otto')?.vario_channel_id ?? null;
    const target = buildListerRouteTarget(window.location.search, readRememberedListerChannel(), installChannelId);
    if (!target) {
      showToast('Lister nicht erreichbar: kein OTTO-Verkaufskanal gefunden.', 'error');
      return;
    }
    try { localStorage.setItem(FOCUS_KEY, JSON.stringify({ listingId, ts: Date.now() })); } catch { /* Lister öffnet dann ohne Auswahl */ }
    sendVarioMessage({ openInSameTab: target });
    setTimeout(() => {
      showToast('VARIO hat den Lister nicht geöffnet. Bitte im Verkaufskanal OTTO den Reiter „Lister“ wählen.', 'info');
    }, 2500);
  }

  // ── „Senden": Angebot an OTTO übertragen (RPC otto_enqueue_push → Worker-Job). ──
  // „Senden" setzt das Listing implizit push-bereit und enqueued den Push in einem Schritt
  // (RPC akzeptiert seit 0248 auch pending/mapping) — kein separater „push-bereit"-Klick mehr.
  async function sendToOtto(id: string): Promise<void> {
    const btn = ctx.contentEl.querySelector<HTMLButtonElement>('#slot-send');
    if (btn) btn.disabled = true;
    setStatusBar(ctx.contentEl, 'loading', 'Sende an OTTO…');
    const { data: jobId, error } = await ctx.supabase.rpc('otto_enqueue_push', { _id: id });
    if (error) {
      const msg = `Senden fehlgeschlagen: ${error.message}`;
      setStatusBar(ctx.contentEl, 'error', msg);
      showToast(msg, 'error');
      if (btn) btn.disabled = false;
      return;
    }
    showToast('An OTTO gesendet — Übertragung läuft…', 'success');
    await refresh(); // zeigt sofort „Wird übertragen"
    // Der Push läuft asynchron im Worker. Ohne Warten bliebe die Anzeige bis zum manuellen
    // Neuladen auf „Wird übertragen". Also den Job pollen und danach den finalen Status rendern.
    if (typeof jobId !== 'string') return;
    try {
      const job = await awaitJob(ctx.supabase, jobId, { timeoutMs: 120_000, pollIntervalMs: 2_000 });
      if (currentSendId !== id) return; // Nutzer hat inzwischen den Artikel gewechselt
      await refresh(); // Status-Zone zeigt jetzt „gelistet" bzw. „fehlgeschlagen"
      if (job.status === 'failed') showToast('Übertragung fehlgeschlagen — Details im Vorschau-Tab.', 'error');
      else showToast('Übertragung abgeschlossen — Status aktualisiert.', 'info');
    } catch {
      if (currentSendId !== id) return;
      await refresh();
      setStatusBar(ctx.contentEl, '', '');
      showToast('Übertragung dauert länger als erwartet — Status ggf. per Neuladen prüfen.', 'info');
    }
  }

  // ── Bereits im Lister → Detail-Pane inline ───────────────────────────────
  function renderInLister(row: InboxRow): void {
    const body = ctx.contentEl.querySelector<HTMLElement>('#slot-body')!;
    // Kein eigenes Status-Banner mehr — die Detailfläche (renderStatusZone) zeigt den
    // Status (Kategorie + grüner Haken / Fehler / „wird übertragen") als einzige Quelle.
    body.innerHTML = `<div id="slot-pane" class="kl-detail-host"></div>`;
    const paneEl = body.querySelector<HTMLDivElement>('#slot-pane')!;
    void renderListingDetailPane(paneEl, {
      supabase: ctx.supabase,
      session: { token: ctx.session.token, tenantId: ctx.session.tenantId },
      supabaseUrl: ctx.supabaseUrl,
    }, row.id, {
      kind: 'slot',
      onListingChanged: () => void refresh(),
      onDeleted: () => void refresh(),
      // Nach Speichern KEIN Pane-Neuaufbau (aktiver Tab + Eingaben bleiben). Slot hat keine
      // Liste → nichts nachzuladen; der Status bleibt bis zur nächsten Navigation/„Senden".
      onSaved: () => { /* no-op */ },
      onSaveHandler: (info) => {
        currentSave = info.save;
        const btn = ctx.contentEl.querySelector<HTMLButtonElement>('#slot-save');
        if (btn) {
          btn.hidden = !info.visible;
          btn.innerHTML = `<span class="v-icon fa-regular fa-floppy-disk"></span> ${info.label}`;
        }
      },
    });
    currentSendId = row.id;
    const snd = ctx.contentEl.querySelector<HTMLButtonElement>('#slot-send');
    if (snd) { snd.hidden = false; snd.disabled = false; }
    const opn = ctx.contentEl.querySelector<HTMLButtonElement>('#slot-open-lister');
    if (opn) opn.hidden = false;
  }

  // ── Noch nicht im Lister → Kategorie-Picker (Tabelle+Filter-Modal) ────────
  async function renderPicker(): Promise<void> {
    currentSave = null;
    currentSendId = null;
    const sb = ctx.contentEl.querySelector<HTMLButtonElement>('#slot-save');
    if (sb) sb.hidden = true;
    const snd = ctx.contentEl.querySelector<HTMLButtonElement>('#slot-send');
    if (snd) snd.hidden = true;
    const opn = ctx.contentEl.querySelector<HTMLButtonElement>('#slot-open-lister');
    if (opn) opn.hidden = true;
    const body = ctx.contentEl.querySelector<HTMLElement>('#slot-body')!;
    body.innerHTML = `
      <div class="v-banner v-banner-info" style="margin-bottom:10px;">
        <span class="v-icon fa-light fa-circle-info"></span>
        <div>Dieser Artikel ist noch nicht im OTTO-Lister. Wähle eine OTTO-Kategorie, um ihn aufzunehmen.</div>
      </div>
      <div class="v-row v-row-sm" style="align-items:center;gap:8px;margin:4px 0 12px;">
        <button type="button" id="otto-cat-pick" class="v-button">
          <span class="v-icon fa-regular fa-folder-tree"></span> Kategorie wählen
        </button>
        <span id="otto-cat-current" class="v-text-muted">— keine Kategorie gewählt —</span>
      </div>
      <button type="button" id="otto-add-btn" class="v-button v-button--primary" disabled>
        <span class="v-icon fa-regular fa-plus"></span> In OTTO-Lister aufnehmen
      </button>`;

    const pickBtn = body.querySelector<HTMLButtonElement>('#otto-cat-pick')!;
    const currentEl = body.querySelector<HTMLSpanElement>('#otto-cat-current')!;
    const addBtn = body.querySelector<HTMLButtonElement>('#otto-add-btn')!;
    let picked: CategoryPick | null = null;

    pickBtn.addEventListener('click', () => openCategoryPicker(ctx.supabase, {
      current: picked?.leaf ?? null,
      onPick: (p) => {
        picked = p;
        currentEl.innerHTML = `<strong>${escapeHtml(p.leaf)}</strong> <span class="v-text-muted">· ${escapeHtml(p.group)}</span>`;
        addBtn.disabled = false;
      },
    }));

    addBtn.addEventListener('click', async () => {
      const pick = picked;
      if (!pick) return;
      addBtn.disabled = true;
      // 1. Vario-Kategorie am Listing setzen (PATCH categoryRefs) + Artikel-Basics holen.
      setStatusBar(ctx.contentEl, 'loading', 'Setze Kategorie am Vario-Listing…');
      let assigned: { varioCategoryId?: string; articleName?: string | null; ean?: string | null; description?: string | null; isVariantParent?: boolean };
      try {
        const res = await fetch(`${ctx.supabaseUrl}/functions/v1/otto-assign-listing-category`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${ctx.session.token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ articleId, listingId, group: pick.group, leaf: pick.leaf }),
        });
        const payload = (await res.json().catch(() => ({}))) as typeof assigned & { error?: { message: string } };
        if (!res.ok || payload.error) {
          setStatusBar(ctx.contentEl, 'error', `Kategorie setzen fehlgeschlagen: ${payload.error?.message ?? httpErrorText(res.status)}`);
          addBtn.disabled = false;
          return;
        }
        assigned = payload;
      } catch (e) {
        setStatusBar(ctx.contentEl, 'error', `Kategorie setzen fehlgeschlagen: ${(e as Error).message}`);
        addBtn.disabled = false;
        return;
      }
      // 2. Lister-Zeile anlegen (mit Vario-Kategorie-ID + Artikel-Basics aus Schritt 1).
      setStatusBar(ctx.contentEl, 'loading', 'Nehme in den OTTO-Lister auf…');
      const { error: upErr } = await ctx.supabase.rpc('otto_upsert_listing_from_slot', {
        _vario_article_id: articleId,
        _vario_listing_id: listingId,
        _vario_category_id: assigned.varioCategoryId ?? null,
        _otto_category: pick.leaf,
        _category_name: pick.leaf,
        _vario_article_name: assigned.articleName ?? null,
        _ean: assigned.ean ?? null,
        _description: assigned.description ?? null,
        _is_variant_parent: assigned.isVariantParent ?? false,
      });
      if (upErr) {
        setStatusBar(ctx.contentEl, 'error', `Aufnehmen fehlgeschlagen: ${upErr.message}`);
        addBtn.disabled = false;
        return;
      }
      setStatusBar(ctx.contentEl, 'success', 'Kategorie gesetzt & aufgenommen.');
      showToast('Kategorie am Vario-Listing gesetzt und in den OTTO-Lister aufgenommen.', 'success');
      await refresh();
    });
  }
}

// ── Markup + Utils ─────────────────────────────────────────────────────────

function renderShell(): string {
  return `
    <style>
      /* Auto-Grow: bridge.css §16 zwingt html,body,main#app auf height:100%+overflow:hidden →
         body.scrollHeight = Viewport statt Inhalt. Höher-spezifischer html.otto-autogrow-Override
         löst die Kette (height:auto), damit der gepostete scrollHeight die echte Inhaltshöhe ist.
         Identisch zu den Einstellungen; kein Eingriff in die geteilte Datei. */
      html.otto-autogrow, html.otto-autogrow body, html.otto-autogrow main#app { height:auto; overflow:visible; }
      html.otto-autogrow main#app > section#content { flex:0 0 auto; }
    </style>
    <div class="kf-page" style="padding:10px;">
      <header class="apphead" style="margin-bottom:8px;">
        <a class="brand" href="https://www.go-ecommerce.de" target="_blank" rel="noopener" title="go eCommerce" style="display:inline-flex;align-items:center;">${KF_LOGO_GOECOMMERCE}</a>
        <img class="otto-logo" src="/otto-market.png" alt="OTTO Market" style="height:20px;" />
        <span class="hdivider"></span>
        <div class="htitle"><div class="heyebrow">OTTO · Lister</div><h1 id="slot-subtitle">Artikel Lister</h1></div>
        <div class="hactions"><span class="otto-slot-conn"><span class="otto-slot-conn-d"></span> Otto verbunden</span><button type="button" id="slot-open-lister" class="otto-slot-open" hidden title="Diesen Artikel im OTTO-Lister des Verkaufskanals öffnen"><span class="v-icon fa-regular fa-arrow-up-right-from-square"></span> Im Lister öffnen</button><button type="button" id="slot-save" class="otto-slot-save" hidden><span class="v-icon fa-regular fa-floppy-disk"></span> Speichern</button><button type="button" id="slot-send" class="otto-slot-send" hidden><span class="v-icon fa-regular fa-paper-plane"></span> Senden</button><span id="slot-status"></span></div>
      </header>
      <div id="slot-body"></div>
    </div>`;
}

function readSlotParams(): { articleId: string | null; listingId: string | null } {
  const q = new URL(window.location.href).searchParams;
  return {
    articleId: q.get('articleId') ?? q.get('article_id') ?? q.get('id'),
    listingId: q.get('listingId') ?? q.get('listing_id'),
  };
}

function setStatusBar(host: HTMLElement, kind: 'loading' | 'success' | 'error' | '', msg: string): void {
  const el = host.querySelector<HTMLElement>('#slot-status');
  if (!el) return;
  if (!kind) { el.innerHTML = ''; return; }
  const icon = kind === 'loading' ? 'fa-spinner-third fa-spin' : kind === 'success' ? 'fa-circle-check' : 'fa-circle-xmark';
  const cls = kind === 'success' ? 'v-text-success' : kind === 'error' ? 'v-text-error' : 'v-text-muted';
  el.innerHTML = `<span class="${cls}" style="font-size:0.88em;"><span class="v-icon fa-regular ${icon}"></span> ${escapeHtml(msg)}</span>`;
}

function escapeHtml(s: string | null | undefined): string {
  if (s === null || s === undefined) return '';
  return String(s)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}
